/**
 * `/help` — support, docs, and shortcut launcher stubs.
 *
 * Per `05-pages.md` §Help: a simple card with support email, a
 * link to in-app docs, and a disabled "Open keyboard shortcuts"
 * button that activates once Stage 7 wires the shortcuts modal.
 */
import { Island } from '@/components/shell/Island';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { Button } from '@/components/ui/button';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { uiDialogs } from '@/lib/stores/ui-dialogs';
import { BookOpen, Keyboard, Lifebuoy, PaperPlaneTilt } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/help/')({
  component: HelpPage,
});

function HelpPage(): ReactElement {
  return (
    <Island breadcrumb={[{ label: 'Site', render: <SiteSwitcherCrumb /> }, { label: 'Help' }]}>
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Help &amp; support</h1>
          <p className="text-sm text-[var(--color-mute)]">
            A few ways to get unstuck. Prefer async? Our docs cover most of the API surface.
          </p>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          <HelpCard
            icon={PaperPlaneTilt}
            title="Email support"
            description="Reach a human within one business day."
            action={
              <Button
                variant="outline"
                render={
                  // biome-ignore lint/a11y/useAnchorContent: content lives in the Button children which Base UI merges into the rendered <a>
                  <a
                    href="mailto:support@clickweave.co"
                    target="_blank"
                    rel="noreferrer noopener"
                  />
                }
              >
                support@clickweave.co
              </Button>
            }
          />
          <HelpCard
            icon={BookOpen}
            title="Developer docs"
            description="Quickstart and the full API reference — all in-app."
            action={
              <Button variant="outline" render={<Link to="/docs" />}>
                Open docs →
              </Button>
            }
          />
          <HelpCard
            icon={Keyboard}
            title="Keyboard shortcuts"
            description="Global palette, nav jumps, audit actions."
            action={
              <Button variant="outline" onClick={() => uiDialogs.open('shortcuts')}>
                Open shortcuts
                <KbdGroup className="ml-2">
                  <Kbd>⇧</Kbd>
                  <Kbd>?</Kbd>
                </KbdGroup>
              </Button>
            }
          />
        </div>

        <section className="flex items-start gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper-alt)] p-4">
          <Lifebuoy
            aria-hidden="true"
            size={24}
            weight="light"
            className="text-[var(--color-mute)]/80"
          />
          <div className="flex-1 text-sm">
            <h2 className="font-semibold text-[var(--color-ink)]">Incident or outage?</h2>
            <p className="text-[var(--color-mute)]">
              Email support with your workspace slug and a sample request id from any toast — we'll
              dig in.
            </p>
          </div>
        </section>
      </div>
    </Island>
  );
}

function HelpCard({
  icon: IconCmp,
  title,
  description,
  action,
}: {
  icon: typeof BookOpen;
  title: string;
  description: string;
  action: ReactElement;
}): ReactElement {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <IconCmp
        aria-hidden="true"
        size={22}
        weight="light"
        className="text-[var(--color-mute)]/80"
      />
      <div className="flex-1 space-y-1">
        <h2 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h2>
        <p className="text-xs text-[var(--color-mute)]">{description}</p>
      </div>
      <div>{action}</div>
    </section>
  );
}
